import * as React from 'react'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'

import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import type LeaveDayTypeForm from 'modules/timeoff/lib/LeaveDayTypeForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'
import LeavesToDateTree from 'modules/timeoff/lib/toDateTree/Leaves'
import leaves from 'modules/timeoff/store/collections/leaves'

import Days from './DayTypes/Days'
import HalfDay from './DayTypes/HalfDay'
import Hours from './DayTypes/Hours'

interface Props {
  readOnly: boolean
  form: LeaveForm
  dayTypeForm: LeaveDayTypeForm
  companyHolidaysMuncher: () => DaysTree
  blockedPeriodsMuncher: () => DaysTree
  timeTaken?: string
  labourUnits: LabourUnitsResult
  isReviewOrEditMode?: boolean
  leaveId: string | undefined
  leaveTypeId: string
}

const DayTypeForm = ({
  readOnly = false,
  form,
  dayTypeForm,
  companyHolidaysMuncher,
  blockedPeriodsMuncher,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
  leaveId,
  leaveTypeId,
}: Props) => {
  const graphqlClient = useGraphQLClient()

  if (!graphqlClient) {
    throw new Error('GraphQL client not found')
  }
  const dayType = dayTypeForm.get('day_type').value

  // Get employee IDs - either single or multiple for bulk creation
  const bulkEmployees = form.get('employees_for_bulk_creation').value || []
  const singleEmployeeId = form.get('employee_id').value
  const isBulkCreation = bulkEmployees.length > 0

  // Get all relevant employee IDs
  const employeeIds = isBulkCreation
    ? bulkEmployees.map((emp: { id: string }) => emp.id)
    : singleEmployeeId
      ? [singleEmployeeId]
      : []

  // NOTE: this is not handling the loading state properly
  const { value: existingLeaves } = useAsyncData(() => {
    const existingLeavesToBlockOverlap = leaves.filter((leaveToFilter) => {
      // Must be for one of the selected employees
      if (!employeeIds.includes(String(leaveToFilter.attributes.employee_id))) {
        return false
      }

      // When editing, exclude the current leave
      if (leaveId) {
        return String(leaveToFilter.attributes.id) !== leaveId
      }

      // When creating, only include leaves of the same type
      return String(leaveToFilter.attributes.leave_type_id) === leaveTypeId
    })

    return new LeavesToDateTree(existingLeavesToBlockOverlap).munch(graphqlClient)
  }, [leaveTypeId, singleEmployeeId, bulkEmployees.length, leaveId, leaves.length])

  const props = {
    form,
    readOnly,
    holidays: companyHolidaysMuncher(),
    blockedPeriods: blockedPeriodsMuncher(),
    existingLeaves: existingLeaves || {},
    timeTaken,
    labourUnits,
    isReviewOrEditMode,
    bulkAdd: isBulkCreation,
  }

  switch (dayType) {
    case 'half_day':
      return <HalfDay {...props} />
    case 'hours':
      return <Hours {...props} />

    default:
      return <Days {...props} />
  }
}

export default observer(DayTypeForm)
